import type { ContentBlock, FileInfo } from '../types/contentBlock';

// ============================================
// 类型定义
// ============================================

/** 可预览的代码语言类别 */
export type PreviewLang = 'html' | 'css' | 'js';

/** 从 Markdown 提取的代码块 */
export interface ExtractedCodeBlock {
  readonly language: string;
  readonly code: string;
}

/** 按语言分类的代码 */
export interface CodeMap {
  readonly html: string;
  readonly css: string;
  readonly js: string;
}

/** 可预览的文件引用（来自 ContentBlock） */
export interface PreviewableFile {
  readonly file: FileInfo;
  readonly lang: PreviewLang;
}

/** 预览数据源：两种来源的统一结构 */
export interface PreviewSource {
  /** Markdown 提取的代码 */
  readonly codeMap: CodeMap | null;
  /** ContentBlock 中的可预览文件列表 */
  readonly files: readonly PreviewableFile[];
  /** 是否有任何可预览内容 */
  readonly hasPreview: boolean;
}

// ============================================
// 常量
// ============================================

/** Markdown 围栏代码块正则 */
const CODE_FENCE_REGEX = /```(\w+)\s*\n([\s\S]*?)```/g;

/** 语言别名 → 标准类别 */
const LANGUAGE_ALIAS: Readonly<Record<string, PreviewLang>> = {
  html: 'html',
  htm: 'html',
  css: 'css',
  scss: 'css',
  less: 'css',
  js: 'js',
  javascript: 'js',
  jsx: 'js',
  ts: 'js',
  typescript: 'js',
  tsx: 'js',
};

/** 可预览的 MIME 类型 → 语言类别 */
const PREVIEWABLE_MIME: Readonly<Record<string, PreviewLang>> = {
  'text/html': 'html',
  'text/css': 'css',
  'text/javascript': 'js',
  'application/javascript': 'js',
  'application/x-javascript': 'js',
};

/** 可预览的文件扩展名 → 语言类别 */
const PREVIEWABLE_EXT: Readonly<Record<string, PreviewLang>> = {
  '.html': 'html',
  '.htm': 'html',
  '.css': 'css',
  '.js': 'js',
  '.jsx': 'js',
};

// ============================================
// Markdown 代码块提取
// ============================================

/**
 * 从 Markdown 文本中提取所有围栏代码块
 */
export const extractAllCodeBlocks = (markdown: string): readonly ExtractedCodeBlock[] => {
  const blocks: ExtractedCodeBlock[] = [];
  const regex = new RegExp(CODE_FENCE_REGEX.source, CODE_FENCE_REGEX.flags);
  let match: RegExpExecArray | null;

  while ((match = regex.exec(markdown)) !== null) {
    blocks.push({
      language: match[1].toLowerCase(),
      code: match[2].trim(),
    });
  }

  return blocks;
};

/**
 * 从 Markdown 中提取可预览的 HTML/CSS/JS 代码
 * 同一语言多次出现时，取最后一个（最新的）
 */
export const extractCodeMapFromMarkdown = (markdown: string): CodeMap | null => {
  const blocks = extractAllCodeBlocks(markdown);

  const result: { html: string; css: string; js: string } = {
    html: '',
    css: '',
    js: '',
  };

  let hasHtml = false;

  for (const block of blocks) {
    const mapped = LANGUAGE_ALIAS[block.language];
    if (mapped) {
      result[mapped] = block.code;
      if (mapped === 'html') hasHtml = true;
    }
  }

  // 必须至少有 HTML 块才认为可预览
  return hasHtml ? result : null;
};

// ============================================
// ContentBlock 文件提取
// ============================================

/**
 * 判断文件是否可预览
 */
const getFileLang = (file: FileInfo): PreviewLang | null => {
  // 先匹配 MIME
  const byMime = PREVIEWABLE_MIME[file.mime_type];
  if (byMime) return byMime;

  // 再匹配扩展名
  const ext = file.extension?.startsWith('.') ? file.extension : `.${file.extension}`;
  const byExt = PREVIEWABLE_EXT[ext.toLowerCase()];
  if (byExt) return byExt;

  return null;
};

/**
 * 从 ContentBlock 列表中提取可预览的文件
 */
export const extractPreviewableFiles = (
  blocks: readonly ContentBlock[] | undefined
): readonly PreviewableFile[] => {
  if (!blocks || blocks.length === 0) return [];

  const files: PreviewableFile[] = [];

  for (const block of blocks) {
    if (block.type === 'file' && block.file) {
      const lang = getFileLang(block.file);
      if (lang) {
        files.push({ file: block.file, lang });
      }
    }

    // ★ 已移除：旧版 agent_thought.messageFiles 逻辑
    // 新架构中文件统一通过 block.type === 'file' 提取
  }

  return files;
};

// ============================================
// 统一提取入口
// ============================================

/**
 * 从消息内容 + ContentBlock 中统一提取预览数据源
 */
export const extractPreviewSource = (
  content: string,
  contentBlocks?: readonly ContentBlock[]
): PreviewSource => {
  const codeMap = extractCodeMapFromMarkdown(content);
  const files = extractPreviewableFiles(contentBlocks);

  return {
    codeMap,
    files,
    hasPreview: codeMap !== null || files.length > 0,
  };
};

// ============================================
// HTML 拼合
// ============================================

/**
 * 将 CodeMap 拼合成完整 HTML 文档
 */
export const assembleHtmlDocument = (code: CodeMap): string => {
  const isFullDocument =
    code.html.toLowerCase().includes('<!doctype') ||
    code.html.toLowerCase().includes('<html');

  if (isFullDocument) {
    let doc = code.html;

    if (code.css) {
      const styleTag = `<style>\n${code.css}\n</style>`;
      doc = doc.includes('</head>')
        ? doc.replace('</head>', `${styleTag}\n</head>`)
        : `${styleTag}\n${doc}`;
    }

    if (code.js) {
      const scriptTag = `<script>\n${code.js}\n<\/script>`;
      doc = doc.includes('</body>')
        ? doc.replace('</body>', `${scriptTag}\n</body>`)
        : `${doc}\n${scriptTag}`;
    }

    return doc;
  }

  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Preview</title>
  ${code.css ? `<style>\n${code.css}\n</style>` : ''}
</head>
<body>
  ${code.html}
  ${code.js ? `<script>\n${code.js}\n<\/script>` : ''}
</body>
</html>`;
};

/**
 * 下载文件内容（文本）
 */
export const fetchFileContent = async (url: string): Promise<string> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`下载文件失败: ${response.status} ${response.statusText}`);
  }
  return response.text();
};

/**
 * 从可预览文件列表构建 CodeMap
 * 异步：需要下载每个文件的内容
 */
export const buildCodeMapFromFiles = async (
  files: readonly PreviewableFile[]
): Promise<CodeMap> => {
  const result: { html: string; css: string; js: string } = {
    html: '',
    css: '',
    js: '',
  };

  const downloads = files.map(async ({ file, lang }) => {
    try {
      const content = await fetchFileContent(file.url);
      result[lang] = content;
    } catch (err) {
      console.error(`[codePreviewExtractor] 下载文件失败: ${file.filename}`, err);
    }
  });

  await Promise.all(downloads);
  return result;
};

/**
 * 合并两个 CodeMap：file 来源优先级更高（覆盖 Markdown 提取的）
 */
export const mergeCodeMaps = (
  fromMarkdown: CodeMap | null,
  fromFiles: CodeMap
): CodeMap => {
  const base = fromMarkdown ?? { html: '', css: '', js: '' };
  return {
    html: fromFiles.html || base.html,
    css: fromFiles.css || base.css,
    js: fromFiles.js || base.js,
  };
};